import React, { useRef, useState, useEffect } from 'react';
import * as faceapi from 'face-api.js';
import { ACTIVE_BRAND } from '../branding';
import './SelfiePage.css';

function SelfiePage({ onComplete, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [error, setError] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceConfidence, setFaceConfidence] = useState(0);
  const detectionIntervalRef = useRef(null);
  const countdownLockedRef = useRef(false);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model/';
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL)
        ]);
        setModelsLoaded(true);
        if (process.env.NODE_ENV !== 'production') {
          console.log('Face detection models loaded');
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to load face-api models:', err);
        }
        setError('Failed to load face detection models');
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    if (modelsLoaded && !capturedImage) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [modelsLoaded]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      // Start face detection
      if (modelsLoaded) {
        startFaceDetection();
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Camera access error:', err);
      }
      setError('Unable to access camera. Please ensure camera permissions are granted.');
    }
  };

  // Continuous face detection
  const startFaceDetection = () => {
    detectionIntervalRef.current = setInterval(async () => {
      if (videoRef.current && canvasRef.current && videoRef.current.readyState === 4) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        
        // Set canvas size to match actual video display dimensions
        const displaySize = { 
          width: video.offsetWidth, 
          height: video.offsetHeight 
        };
        
        canvas.width = displaySize.width;
        canvas.height = displaySize.height;
        
        // Clear previous drawings
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        try {
          // Detect face with landmarks
          const detection = await faceapi
            .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks();

          if (detection) {
            // Use faceapi's built-in resize with display dimensions
            const resizedDetection = faceapi.resizeResults(detection, displaySize);
            
            // Don't draw anything - just use for detection
            // faceapi.draw.drawDetections(canvas, resizedDetection);
            // faceapi.draw.drawFaceLandmarks(canvas, resizedDetection);
            
            const confidence = detection.detection.score * 100;
            setFaceDetected(true);
            setFaceConfidence(confidence);
            
            // Start countdown if confidence is above 90% and not already locked
            if (!countdownLockedRef.current && confidence >= 90) {
              if (process.env.NODE_ENV !== 'production') {
                console.log(`Starting countdown - confidence: ${confidence.toFixed(1)}%`);
              }
              countdownLockedRef.current = true;
              setCountdown(3);
            }
          } else {
            setFaceDetected(false);
            setFaceConfidence(0);
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Detection error:', err);
          }
        }
      }
    }, 100); // Check every 100ms
  };

  // Countdown effect for auto-capture
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Auto-capturing photo...');
      }
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && stream) {
      // Auto-capture when countdown reaches 0
      console.log('Auto-capturing photo...');
      countdownLockedRef.current = false;
      setTimeout(() => {
        if (stream) {
          capturePhoto();
        }
      }, 100);
    }
  }, [countdown, stream]);

  const stopCamera = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    setFaceDetected(false);
    setCountdown(null);
    countdownLockedRef.current = false;
    
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleCapture = () => {
    if (countdown !== null) return; // Already counting down
    capturePhoto();
  };

  const capturePhoto = () => {
    // Stop detection
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    const video = videoRef.current;
    const captureCanvas = document.createElement('canvas');
    const context = captureCanvas.getContext('2d');

    if (video) {
      captureCanvas.width = video.videoWidth;
      captureCanvas.height = video.videoHeight;
      
      // Draw video frame to canvas (flip horizontally for mirror effect)
      context.translate(captureCanvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(video, 0, 0);
      
      // Convert to base64
      const imageData = captureCanvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageData);
      
      // Stop camera
      stopCamera();
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCountdown(null);
    startCamera();
  };

  const handleConfirm = () => {
    onComplete(capturedImage);
  };

  const handleCancelClick = () => {
    stopCamera();
    onCancel();
  };

  return (
    <div className="selfie-page">
      <div className="selfie-content">
        <div className="selfie-card">
          <h2 className="selfie-title" style={{ color: ACTIVE_BRAND.colors.primary }}>
            TAKE A SELFIE
          </h2>
          <p className="selfie-instruction">
            {!modelsLoaded ? 'Loading face detection...' : 'Position your face in the center and ensure good lighting'}
          </p>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="camera-container">
            {!capturedImage ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="video-preview"
                />
                <canvas
                  ref={canvasRef}
                  className="webcam-canvas"
                />
                <div className="face-guide">
                  <div className="face-oval"></div>
                </div>
                {countdown !== null && countdown > 0 && (
                  <div className="countdown-overlay">
                    <div className="countdown-number">{String(countdown)}</div>
                  </div>
                )}
                {stream && (
                  <div className="detection-status">
                    {faceDetected ? (
                      faceConfidence >= 90 ? (
                        <div style={{ color: '#48bb78', fontWeight: 600 }}>
                          ✓ Face detected
                        </div>
                      ) : (
                        <div style={{ color: '#ed8936', fontWeight: 500 }}>
                          👤 Get closer to camera and look forward
                        </div>
                      )
                    ) : (
                      <div style={{ color: '#ed8936', fontWeight: 500 }}>
                        📷 Position your face in the frame
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <img src={capturedImage} alt="Captured selfie" className="captured-image" />
            )}
          </div>

          <div className="selfie-actions">
            {!capturedImage ? (
              <>
                <button
                  className="cancel-button"
                  onClick={handleCancelClick}
                  style={{
                    borderColor: ACTIVE_BRAND.colors.buttonBg,
                    color: ACTIVE_BRAND.colors.buttonBg
                  }}
                >
                  Cancel
                </button>
                <button
                  className="capture-button"
                  onClick={handleCapture}
                  disabled={!stream || !modelsLoaded}
                  style={{
                    backgroundColor: ACTIVE_BRAND.colors.buttonBg,
                    color: ACTIVE_BRAND.colors.buttonText
                  }}
                >
                  📸 Capture Now
                </button>
              </>
            ) : (
              <>
                <button
                  className="retake-button"
                  onClick={handleRetake}
                  style={{
                    borderColor: ACTIVE_BRAND.colors.buttonBg,
                    color: ACTIVE_BRAND.colors.buttonBg
                  }}
                >
                  Retake
                </button>
                <button
                  className="confirm-button"
                  onClick={handleConfirm}
                  style={{
                    backgroundColor: ACTIVE_BRAND.colors.buttonBg,
                    color: ACTIVE_BRAND.colors.buttonText
                  }}
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SelfiePage;
